import { WeatherData, WeatherCondition, LocationData, WeatherError, WeatherErrorType } from './types';
import { CacheManager, WEATHER_CACHE_KEYS } from './utils/cache';
import { RetryManager } from './utils/retry';
import { WeatherConfigManager } from './config';
import { PerformanceMonitor } from './utils/performanceMonitor';
import { WeatherErrorHandler, FallbackWeatherData } from './utils/errorHandler';
import { WeatherAPIManager } from './apiManager';

export class WeatherService {
  static async getWeatherData(location: LocationData): Promise<WeatherData | FallbackWeatherData> {
    PerformanceMonitor.startTimer('weather_data_fetch', { 
      city: location.city, 
      country: location.country 
    });

    try {
      // Try to get from cache first
      const cached = CacheManager.get<WeatherData>(WEATHER_CACHE_KEYS.WEATHER);
      if (cached) {
        PerformanceMonitor.endTimer('weather_data_fetch');
        return cached;
      }

      const config = WeatherConfigManager.getConfig();
      
      if (!WeatherConfigManager.isConfigured()) {
        throw new WeatherError(
          WeatherErrorType.WEATHER_API_FAILED,
          'No weather APIs configured'
        );
      }

      const weatherData = await PerformanceMonitor.measureAPICall(
        'weather_api',
        'multiple_endpoints',
        'GET',
        () => RetryManager.withRetry(
          () => WeatherAPIManager.fetchWeatherData(location),
          {
            ...RetryManager.createProgressiveRetryOptions('weather'),
            onRetry: (error, attempt) => {
              console.warn(`🌤️ Weather API retry ${attempt}: ${error.message}`);
            }
          }
        )
      );

      // Cache the successful result
      CacheManager.set(WEATHER_CACHE_KEYS.WEATHER, weatherData, config.weatherCacheDuration);
      PerformanceMonitor.endTimer('weather_data_fetch');
      
      if (config.debugMode) {
        console.log('🌤️ Weather data fetched successfully:', {
          temperature: weatherData.temperature,
          condition: weatherData.condition,
          location: `${weatherData.location.city}, ${weatherData.location.country}`
        });
      }
      
      return weatherData;
    } catch (error) {
      PerformanceMonitor.endTimer('weather_data_fetch');
      return this.handleWeatherError(error as Error, location);
    }
  }

  private static handleWeatherError(error: Error, location: LocationData): WeatherData | FallbackWeatherData {
    // Try multiple fallback strategies in order of preference
    
    // 1. Try expired cache first
    const expiredCache = this.getExpiredCache();
    if (expiredCache) {
      console.warn('🌤️ Using expired weather cache due to API failure');
      return expiredCache;
    }

    // 2. Try to create location-specific fallback
    const locationFallback = this.createLocationBasedFallback(location);
    if (locationFallback) {
      console.warn('🌤️ Using location-based weather fallback');
      return locationFallback;
    }

    // 3. Use comprehensive error handling for final fallback
    const errorResult = WeatherErrorHandler.handle(error, 'weather_service', {
      context: 'weather_data_fetch',
      enableRetry: false // Already retried above
    });

    console.warn('🌤️ Using default weather fallback:', errorResult.userMessage);
    return errorResult.fallbackData as FallbackWeatherData;
  }

  private static getExpiredCache(): WeatherData | null {
    try {
      const expiredCache = localStorage.getItem(WEATHER_CACHE_KEYS.WEATHER);
      if (expiredCache) {
        const parsed = JSON.parse(expiredCache);
        // Only use if it's not too old (max 24 hours)
        const cacheAge = Date.now() - new Date(parsed.timestamp).getTime();
        if (cacheAge < 24 * 60 * 60 * 1000) {
          return parsed.data;
        }
      }
    } catch (parseError) {
      console.warn('🌤️ Failed to parse expired cache:', parseError);
    }
    return null;
  }

  private static createLocationBasedFallback(location: LocationData): FallbackWeatherData | null {
    // Create reasonable weather defaults based on location and season
    const now = new Date();
    const month = now.getMonth(); // 0-11
    const hour = now.getHours();
    
    // Determine season in northern hemisphere (reverse for southern)
    const isWinter = month >= 11 || month <= 1;
    const isSummer = month >= 5 && month <= 7;
    const isNight = hour < 6 || hour > 20;
    
    // Base temperature on season and location
    let baseTemp = 15; // Default moderate temperature
    
    if (location.coordinates.latitude > 40) { // Northern regions
      baseTemp = isWinter ? 5 : isSummer ? 25 : 15;
    } else if (location.coordinates.latitude < 20) { // Tropical regions
      baseTemp = isWinter ? 20 : isSummer ? 30 : 25;
    }
    
    // Determine condition based on season
    let condition: WeatherCondition = 'default';
    if (isWinter) {
      condition = 'cloudy';
    } else if (isSummer) {
      condition = isNight ? 'clear-night' : 'clear-day';
    } else {
      condition = 'partly-cloudy';
    }

    return {
      temperature: baseTemp,
      condition,
      description: this.getSeasonalDescription(condition, isWinter, isSummer),
      humidity: 50,
      windSpeed: 5,
      location: {
        city: location.city,
        country: location.country,
        coordinates: {
          lat: location.coordinates.latitude,
          lon: location.coordinates.longitude
        }
      },
      timestamp: now,
      isFallback: true,
      fallbackReason: 'location_based_estimate'
    };
  }

  private static getSeasonalDescription(
    condition: WeatherCondition, 
    isWinter: boolean, 
    isSummer: boolean
  ): string {
    const descriptions: Record<WeatherCondition, { winter: string; summer: string; default: string }> = {
      'clear-day': { winter: 'Soleado y fresco', summer: 'Soleado y cálido', default: 'Despejado' },
      'clear-night': { winter: 'Noche despejada', summer: 'Noche cálida', default: 'Noche despejada' },
      'cloudy': { winter: 'Nublado y fresco', summer: 'Nublado', default: 'Nublado' },
      'partly-cloudy': { winter: 'Parcialmente nublado', summer: 'Parcialmente nublado', default: 'Parcialmente nublado' },
      'rain': { winter: 'Lluvia fría', summer: 'Lluvia de verano', default: 'Lluvia' },
      'snow': { winter: 'Nevando', summer: 'Nevando', default: 'Nevando' },
      'fog': { winter: 'Niebla matutina', summer: 'Niebla', default: 'Niebla' },
      'default': { winter: 'Condiciones moderadas', summer: 'Condiciones agradables', default: 'Condiciones moderadas' }
    };

    const desc = descriptions[condition];
    if (isWinter) return desc.winter;
    if (isSummer) return desc.summer;
    return desc.default;
  }



  static clearWeatherCache(): void {
    CacheManager.clear(WEATHER_CACHE_KEYS.WEATHER);
  }

  static clearAllCache(): void {
    CacheManager.clearAll();
  }
}